import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Pagination, Table, Tabs, Tag as AntdTag } from 'antd';
import { Link, useHistory, useParams } from 'react-router-dom';

import FormPopup from '../../components/Popup/FormPopup';
import Tag from '../../components/Tag/Tag';
import Popup from '../../components/Popup/Popup';
import ProfileCard from '../../components/Cards/ProfileCard';
import Icon from '../../components/Icon/Icon';
import Spinner from '../../components/Spinner/Spinner';
import UserAverageRating from '../../components/UserAverageRating/UserAverageRating';

import { Role } from '../../Helpers/AuthorizationDataProvider';
import { Rating, Score, User, UserProfileModel, UserProfileResponse } from './BackofficeUserProfile.model';

import useUserProfile, { pageSize } from './useBackofficeUserProfile';

import styles from './BackofficeUserProfile.module.scss';
import moment from "moment";

const BackofficeUserProfile = () => {
	const intl = useIntl();
	const { id } = useParams<{ id: string }>();
	const {
		deleteUser,
		editUserProfile,
		getI18nLanguageName,
		isAuthorized,
		onChangeRatingsPagination,
		state,
		toggleDeletePopup,
		toggleEditForm
	} = useUserProfile({ id: id });
	const {
		averageScore,
		editFormFields,
		editFormActions,
		loading,
		profile,
		profileResponse,
		ratings,
		ratingsPagination,
		showDeletePopup,
		showEditForm
	} = state;

	const history = useHistory();

	const columns: { [key: string]: any } = {
		firstColumn: [
			{ id: 'name' },
			{ id: 'email' },
			{ id: 'role', type: 'tag' },
			{ id: 'location' },
			{ id: 'languages', type: 'languageList' },
			{ id: 'currentPosition' },
		],
		secondColumn: [{ id: 'introduction' }],
		thirdColumn: [
			{ id: 'researchAreas', type: 'tagList' },
			{ id: 'workExperience', type: 'list' },
			{ id: 'education', type: 'list' },
			{ id: 'mentoringScopes', type: 'tagList' },
		],
	};

	const getTagColorByRole = (role: Role) => {
		switch (role) {
			case 'Mentee': return 'grey';
			case 'Mentor': return 'blue';
			case 'Admin': return 'red';
		}
	}

	const renderChips = (chips: string[], category?: string) => {
		return chips.map((field: string, index: number) => (<AntdTag
			key={`${field}${index}`}
			style={{
				border: 'none',
				textTransform: 'uppercase'
			}}>{category ? <FormattedMessage id={`${category}.${field}`} /> : field}</AntdTag>))
	}

	const renderFieldContent = (field: any, profile: UserProfileResponse | UserProfileModel) => {
		switch (field.type) {
			case "tag": return profile[field.id] && (<div><Tag
				color={getTagColorByRole(profile.role)}
			>
				<FormattedMessage id={`role.${profile.role}`} />
			</Tag></div>);
			case "tagList": return profile[field.id] && (<div>
				{renderChips(profile[field.id], field.id)}
			</div>);
			case "languageList": return profile[field.id] && (<div>
				{renderChips(profile[field.id].map((item: any) => getI18nLanguageName(item)))}
			</div>);
			case "list": return profile[field.id] && (<div>
				{profile[field.id].map((item: any, index: number) => {
					return <div key={`profileField${index}`}> {item}{index !== profile[field.id].length - 1 && ','}<br /></div>
				}
				)}
			</div>)
			default: return <div>{profile[field.id]}</div>;
		}
	}
	const renderField = (field: any, profile: UserProfileResponse | UserProfileModel) => {
		return (
			<div className={styles.field} key={field.id}>
				<span className={styles.label}>
					{intl.formatMessage({ id: `userLabels.${field.id}` })}
				</span>
				{profile[field.id] && profile[field.id][0] ? renderFieldContent(field, profile) :
					<div className={styles.placeholder}><FormattedMessage id="userLabels.placeholder" /></div>}
			</div>
		)
	}

	const renderColumns = (profile: UserProfileResponse | UserProfileModel) => {
		return Object.keys(columns).map((column, index) => {
			return <div className={styles.columnWrapper} key={column}>
				{index !== 0 && <Divider className={styles.verticalDivider} type="vertical" />}
				<div className={styles.column}>
					{columns[column].map((field: any) => renderField(field, profile))}
				</div>
			</div>
		})
	}

	const getBadgeByScore = (score: Score) => {
		switch (score) {
			case 'One':
			case 'Two': return <div className={styles.score}>
				<Icon name="bad-star" type="custom" color="gray" width="20px" height="20px" />
				<span><FormattedMessage id="ratings.score.substandard" /></span>
			</div>
			case 'Three':
			case 'Four': return <div className={styles.score}>
				<Icon name="star" type="solid" color="accent" width="16px" height="16px" />
				<span><FormattedMessage id="ratings.score.good" /></span>
			</div>
			case 'Five': return <div className={styles.score}>
				<Icon name="stars" type="solid" color="blue" width="16px" height="16px" />
				<span><FormattedMessage id="ratings.score.exceptional" /></span>
			</div>
		}
	}

	const renderRatings = (ratings: Rating[]) => {
		const columnsRatings = [
			{
				dataIndex: 'user',
				render: (user: User) => (<div className={styles.flexCell}>
					<div className={styles.avatar}>
						{user.imageId && !user.isDeleted ? <img src={user.imageId} alt="" /> :
							<Icon
								color="light"
								height="12px"
								name="user"
								width="12px"
							/>}
					</div>
					{!user.isDeleted ?  `${user.firstName} ${user.lastName}` : intl.formatMessage({id: "userLabels.deletedUser"})}
				</div>),
				title: intl.formatMessage({ id: 'table.column.mentor' }),
			},
			{
				dataIndex: 'score',
				render: (score: Score) => (
					getBadgeByScore(score)
				),
				title: intl.formatMessage({ id: 'table.column.score' }),
			},
			{
				dataIndex: 'ratedAt',
				render: (ratedAt: string) => (
				 moment(ratedAt).format("DD MMM YYYY")
				),
				title: intl.formatMessage({ id: 'table.column.date' }),
			}];
		
		return <Tabs >
			<Tabs.TabPane key="1" tab={<FormattedMessage id="navigation.reviews" />}>
				<Table
					columns={columnsRatings}
					dataSource={ratings}
					pagination={false}
					onRow={(rowData: Rating) => {
						return {
							onClick: () => {
								history.push(`/rating/${rowData.user.userId}/${id}`);
							},
						};
					}}
				/>
				<div className={styles.paginationWrapper}>
					<Pagination
						current={ratingsPagination.page}
						pageSize={pageSize}
						total={ratingsPagination.total}
						onChange={onChangeRatingsPagination}
						showSizeChanger={false}
					/>
				</div>
			</Tabs.TabPane>
		</Tabs>
	}
	
	return (
		<div className={styles.pageContainer}>
			<div className={`${styles.pageSider} ${styles.topBorder} ${styles.noPadding} ${styles.fitContent}`}>
				{profile && <ProfileCard
					actions={averageScore ? [
						<div className={styles.actions}>
							<UserAverageRating score={averageScore} />
						</div>
					] : []}
					fitContainer
					data={{
						education: profile.education,
						imageId: profile.imageId,
						location: profile.location,
						name: `${profile.firstName} ${profile.lastName}`,
						researchAreas: profile.researchAreas,
					}}
				/>}
			</div>

			<div className={styles.pageContent}>
				<div className={`${styles.mobileTopper} ${styles.topBorder} ${styles.noPadding}`}>
					{profile && <ProfileCard
						actions={averageScore ? [
							<div className={styles.actions}>
								<UserAverageRating score={averageScore} />
							</div>
						] : []}
						fitContainer
						data={{
							education: profile.education,
							imageId: profile.imageId,
							location: profile.location,
							name: `${profile.firstName} ${profile.lastName}`,
							researchAreas: profile.researchAreas,
						}}
					/>}
				</div>
				<div className={styles.mainActions}>
					<div className={styles.breadcrumbs}>
						<Link to="/users"><FormattedMessage id="navigation.users" /> /</Link>
						<div>
							<span>{profile?.name.slice(0, profile?.name.indexOf(' '))}'s </span>
							<b><FormattedMessage id="navigation.profile" /></b>
						</div>
					</div>
					<div className={styles.actions}>
						{isAuthorized(['DeleteUsers']) && <Button danger size="large" onClick={toggleDeletePopup}><FormattedMessage id="buttons.deleteUser" /></Button>}
						<Button size="large" onClick={toggleEditForm}><FormattedMessage id="buttons.editUser" /></Button>
					</div>
				</div>

				<div className={styles.content}>
					<Spinner spinning={loading} />
					<div className={styles.applicationDateWrapper}>
						<FormattedMessage id="userLabels.registeredOn" />
						<span className={styles.applicationDate}> {moment(profile?.acceptedAt).format("DD MMM YYYY")} </span>
						<FormattedMessage id="basics.as" />
						<span className={styles.tag}>
							{profile &&
								<Tag
									color={getTagColorByRole(profile?.role)}
								>
									{profile?.role}
								</Tag>
							}
						</span>
					</div>
					<div className={styles.fields}>
						{profile && renderColumns(profile)}
					</div>
				</div>
				{ratings && renderRatings(ratings)}
			</div>

			{showEditForm &&
				<FormPopup
					actions={editFormActions}
					title={{ action: 'edit', target: 'profile' }}
					editData={profileResponse}
					fields={editFormFields}
					onCancel={toggleEditForm}
					onSubmitForm={editUserProfile} />}

			{showDeletePopup &&
				<Popup
					actions={<>
						<Button size="large" type="default" onClick={toggleDeletePopup}><FormattedMessage id="buttons.cancel" /></Button>
						<Button danger size="large" type="primary" onClick={() => { deleteUser(id); toggleDeletePopup(); }}><FormattedMessage id="buttons.delete" /></Button>
					</>}
					onClose={toggleDeletePopup}
					title={{ action: 'delete', target: 'user' }}>
			
				<div className={styles.popupContent}>
					<FormattedMessage
						id="userProfile.deleteUserQuestion"
						values={{ b: (...chunks: any) => <b><i>{chunks}</i></b>, name: profile?.name }}
					/>
				</div>
			</Popup>}
		</div>)
}

export default BackofficeUserProfile;